import React, {Component} from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import moment from 'moment';
import axios from 'axios';
import URI from 'urijs';
import gql from 'graphql-tag';

import gqlClient from 'js/graphql/client/gqlClient';
import {propOr} from 'js/common/object_helper';
import {track} from 'js/common/analytics';
import {POST} from 'js/common/proptypes/post_props';
import {ENUMS, URLS} from 'js/common';
import Logger from 'js/common/logger';
import * as ShareTokenApiClient from 'js/components/share_content_modal/share_token_api_client';

// Components
import ShareContentModal from 'js/components/share_content_modal';
import Notification from 'js/components/notification';
import NavigationMenu from 'js/components/navigation_menu';
import AnalyticsHeaderContainer from './header';
import AnalyticsContainer from './analytics';
import AnalyticsPostsContainer from './posts/index';
import ProgressBar from './progress_bar';
import checkBrandUserSession from "../../../brand_session";
import {inIframe, isIframeMode} from '../../../common/url_helper';

require('scss/campaign_analytics_v2.scss');

const {navigationMenu, postsWithSparkAuthStatus, tiktokCampaignCode, tiktokInviteLink} = window.sn;


class CampaignAnalyticsPage extends Component {
    constructor(props) {
        super(props);

        const preselectedPost = R.pipe(
            R.path(['analyticsData', 'post_analytics']),
            R.defaultTo([]),
            R.find(R.propEq('post_id', parseInt(this.props.reopenPostId, 10))),
            R.defaultTo({})
        )(props);

        this.state = {
            currentSortColumn: null,
            loading: false,
            notifications: [],
            analyticsData: R.dissoc('post_analytics', props.analyticsData),
            /** initially sort posts by ops score, descending */
            posts: R.pipe(
                R.path(['analyticsData', 'post_analytics']),
                R.sortBy(this.pathFuncForMetricFromPost.ops_post_score),
                R.reverse
            )(props),
            selectedPost: preselectedPost,
            showShareCampaign: false,
            showContentModal: R.complement(R.isEmpty)(preselectedPost),
            showDownloadSurvey: false,
            showDownloadSurveyThankYou: false,
            showBidAndBudgetRow: true,
            selectedStart: moment().unix(),
            selectedEnd: moment().unix(),
            dateSelectedIdx: 0,
            dateSelectActive: false,
            genericShareableLink: props.genericShareableLink,
            shareOptions: props.shareOptions,
            showContentComment: false,
            selectedPostComment: '',
            campaignType: null,
            tiktokCampaignCode,
            tiktokInviteLink
        };
    }

    getCampaignType = async () => {
        const {analyticsData} = this.props;

        try {
            await checkBrandUserSession();

            if (gqlClient.client === undefined) {
                await gqlClient.createClient();
            }

            const {data} = await gqlClient.client.query({
                query: gql`
                query campaignType($campaignId: ID!) {
                  campaignType(campaignId: $campaignId){
                    campaignType
                  }
                }
            `,
                variables: {
                    campaignId: analyticsData.campaign_id
                }
            });

            this.setState({campaignType: data.campaignType.campaignType});

            if (data.campaignType.campaignType === 'tiktok') {
                // Process spark ad data for tiktok campaign
                this.getSparkAdAuthStatus();
            }
        }
        catch (err) {
            console.error('Error while fetching campaign type: ', err);
        }
    }

    getSparkAdAuthStatus = async () => {
        if (postsWithSparkAuthStatus) {
            // Update posts with new spark auth data
            const updatedPosts = this.state.posts.map((post)=> {

                const postWithSparkStatus = postsWithSparkAuthStatus.find((sparkAd) =>
                    sparkAd.post_id === post.post_id);
                if (postWithSparkStatus && postWithSparkStatus.tagged) {
                    const fullUrl = postWithSparkStatus.distributed_media_url;
                    if (fullUrl) {
                        const url = fullUrl.split('?')[0];
                        // We have the distributed_media_url, get the video ID
                        // and add spark ad data to the post
                        const sparkData = {
                            authStatus: postWithSparkStatus.spark_ads_auth_status,
                            authData: postWithSparkStatus.spark_ads_data,
                            videoId: url.substring(url.lastIndexOf('/') + 1)
                        };
                        post.spark_ad = sparkData;
                    }
                }

                return post;
            });

            this.setState({posts: updatedPosts});
        }
    }

    handleRequestSparkAdAuth = async (videoId) => {
        try {
            await checkBrandUserSession();

            if (gqlClient.client === undefined) {
                await gqlClient.createClient();
            }

            const {data} = await gqlClient.client.mutate({
                mutation: gql`
                mutation tiktokRequestSparkAdsAuthorization($videoId: String!) {
                    tiktokRequestSparkAdsAuthorization(videoId: $videoId) {
                        code
                        message
                        requestId
                    }
                }
                `,
                variables: {
                    videoId: videoId
                }
            });

            const result = data.tiktokRequestSparkAdsAuthorization;

            if (result && result.code === 0) {
                const {notifications} = this.state;
                const updatedNotifications = notifications.concat([
                    {
                        id: new Date().getTime(),
                        message: 'Spark Ad authorization requested!',
                        type: 'success'
                    }
                ]);
                this.setState({notifications: updatedNotifications});

            } else {
                // Error
                throw new Error(result);
            }
        }
        catch (err) {
            this.notify({
                message: `Sorry, something went wrong while requesting
                    Stark Ad Auth for the video. Please try again later.`,
                type: 'error'
            });
            console.error(`Error while requesting stark ad auth for video ${videoId}`, err);
        }
    }


    componentDidMount() {
        this.getCampaignType();
        const isEmbedded = inIframe();

        if (isEmbedded && !isIframeMode) {
            document.body.classList.add('embedded-mode');
        }
    }

    handleShareCampaignShow = () => {
        this.setState({
            showShareCampaign: true
        });
    };

    handleShareCampaignHide = () => {
        this.setState({
            showShareCampaign: false
        });
    };

    handleDateRangeChange = (start, end, selectedIdx) => {
        const {analyticsData, retrieveAnalyticsUrlV2} = this.props;
        const baseUrl = retrieveAnalyticsUrlV2.replace(
            '__CAMPAIGN_ID__',
            analyticsData.campaign_id
        );
        const url = URI(baseUrl)
            .search({
                start,
                end
            })
            .toString();

        this.setState({
            loading: true
        });

        axios
            .get(url)
            .then((response) => {
                const {data} = response;

                const dateSelectActive = selectedIdx >= 1;

                this.setState({
                    selectedStart: start,
                    selectedEnd: end,
                    dateSelectedIdx: selectedIdx,
                    dateSelectActive,
                    analyticsData: R.dissoc('post_analytics', data.analytics),
                    posts: data.analytics.post_analytics,
                    loading: false
                });
            })
            .catch((err) => {
                this.setState({
                    loading: false
                });
            });
    };

    hideDateSelect = () => {
        this.setState({
            dateSelectActive: false
        });
    };

    handleGetShareableLinkClick = (e) => {
        e.preventDefault();

        ShareTokenApiClient.getGenericShareableLink().then((link) => {
            track(this.props.email, 'Brand - generated share link', {
                'Brand Name': this.props.analyticsData.brand_name,
                'Campaign Funnel Id': this.props.analyticsData.campaign_id,
                'Campaign Funnel Name': this.props.analyticsData.campaign_name
            });

            this.setState({
                genericShareableLink: link
            });
        });
    };

    handleRemoveShareableLinkClick = (e) => {
        e.preventDefault();
        if (
            !confirm(
                'If you remove this link, it will remove access for anyone that has been sent the link in the past'
            )
        ) {
            return;
        }

        ShareTokenApiClient.deleteGenericShareableLink().then((success) => {
            if (success) {
                this.setState({
                    genericShareableLink: null
                });
            }
        });
    };

    handleShareSucceed = () => {
        const {notifications} = this.state;
        const updatedNotifications = notifications.concat([
            {
                id: new Date().getTime(),
                message: 'Nice! You have successfully shared your campaign!',
                type: 'success'
            }
        ]);

        this.setState({
            showShareCampaign: false,
            notifications: updatedNotifications
        });
    };

    displayEmailDeletedSuccess = () => {
        const {notifications} = this.state;
        const updatedNotifications = notifications.concat([
            {
                id: new Date().getTime(),
                message: 'The email has been removed from the list.',
                type: 'success'
            }
        ]);

        this.setState({
            notifications: updatedNotifications
        });
    };

    notify = (notification) => {
        const {notifications} = this.state;
        const updatedNotifications = notifications.concat([
            R.merge(
                {
                    id: new Date().getTime(),
                    type: 'success'
                },
                notification
            )
        ]);

        this.setState({
            notifications: updatedNotifications
        });
    };

    setPostScore = (
        postScore,
        postId,
        onSuccessfulScoreCallback = () => {},
        onFailedScoreCallback = () => {}
    ) => {
        const {posts, notifications} = this.state;
        const {isAdmin, postScoreUrl} = this.props;

        if (isAdmin) {
            return;
        }

        axios
            .post(postScoreUrl.replace('__ID__', postId), {
                postScore
            })
            .then(({data: {post: postResponse}}) => {
                const updatedPostIndex = R.findIndex(
                    (updatedPost) => updatedPost.post_id === postResponse.post_id
                )(posts);

                const updatedPosts = R.adjust(
                    R.pipe(
                        R.assocPath(['post_user_score'], postResponse.score),
                        R.assocPath(['avg_post_user_score'], postResponse.avg_post_user_score)
                    ),
                    updatedPostIndex,
                    posts
                );

                const selectedPost = updatedPosts[updatedPostIndex];

                this.setState(
                    {
                        notifications: R.append(
                            {
                                id: new Date().getTime(),
                                message: 'Your score has been saved.',
                                type: 'success'
                            },
                            notifications
                        ),
                        posts: updatedPosts,
                        selectedPost,
                        selectedPostComment:
                            postResponse.post_user_comment && R.head(postResponse.post_user_comment)
                    },
                    () => onSuccessfulScoreCallback(postResponse)
                );
            })
            .catch(() => {
                this.notify({
                    message: 'There was an issue trying to save your score. Please try again.',
                    type: 'error'
                });

                onFailedScoreCallback();
            });
    };

    setContentComment = (
        {content_metadata_id, text},
        onSuccessfulCommentCallback = () => {},
        onFailedCommentCallback = () => {}
    ) => {
        const {email: userEmail} = this.props;

        axios
            .post(URLS.upsertContentMetadataComment, {
                content_metadata_id,
                messages: [text],
                comment_source: ENUMS.ContentMetadataCommentSource.BRAND_SCORING
            })
            .then(() => {
                track(userEmail, 'Brand Submitted Content Feedback', {
                    'Brand content score feedback': text
                });

                this.notify({
                    message: 'Your comment was successfully submitted.',
                    type: 'success'
                });

                onSuccessfulCommentCallback();
            })
            .catch((response) => {
                Logger.error('Error submitting content comment from brand scoring modal', {
                    response,
                    content_metadata_id,
                    comment: text
                });

                this.notify({
                    message: 'There was a problem submitting your comment, please try again.',
                    type: 'error'
                });

                onFailedCommentCallback();
            });
    };

    handleSubmitContentDownloadSurvey = (postId, formValue) => {
        const {createNewDownloadSurveyUrl} = this.props;

        const url = createNewDownloadSurveyUrl.replace('__id__', postId);

        axios
            .post(url, formValue)
            .then(() => {
                const {notifications} = this.state;
                const updatedNotifications = notifications.concat([
                    {
                        id: new Date().getTime(),
                        message: 'Thanks, your feedback helps us improve our product!',
                        type: 'success'
                    }
                ]);

                this.setState({
                    notifications: updatedNotifications,
                    showDownloadSurvey: false,
                    showDownloadSurveyThankYou: true
                });
            })
            .catch(() => {
                this.closeContentModal();
            });
    };

    messageSuccessfulUpload = (response) => {
        const {message} = response.data || {};
        this.notify({message: message || 'Thank you for uploading your content.'});
    };

    messageUnsuccessfulUpload = () => {
        this.notify({
            message: 'Sorry, something went wrong uploading your content. Please try again later.',
            type: 'error'
        });
    };

    shareToFacebookPagesAccount = (url, accountId, accountName, formValue, onComplete) => {
        axios
            .post(url, {
                account_id: accountId,
                account_name: accountName,
                caption: formValue.caption
            })
            .then(this.messageSuccessfulUpload)
            .catch(this.messageUnsuccessfulUpload)
            .then(onComplete);
    };

    shareToFacebookAdManager = (url, accountId, accountName, onComplete) => {
        axios
            .post(url, {
                account_id: accountId,
                account_name: accountName
            })
            .then(this.messageSuccessfulUpload)
            .catch(this.messageUnsuccessfulUpload)
            .then(onComplete);
    };

    shareToTwitterAdManager = (url, accountId, accountName, onComplete) => {
        axios
            .post(url, {
                account_id: accountId,
                account_name: accountName
            })
            .then(this.messageSuccessfulUpload)
            .catch(this.messageUnsuccessfulUpload)
            .then(onComplete);
    };

    shareToSnapchatAdManager = (url, accountId, accountName, onComplete) => {
        axios
            .post(url, {
                account_id: accountId,
                account_name: accountName
            })
            .then(this.messageSuccessfulUpload)
            .catch(this.messageUnsuccessfulUpload)
            .then(onComplete);
    };

    shareToPinterest = (url, pin, onComplete) => {
        axios
            .post(url, pin)
            .then((response) => {
                const {message, pinUrl} = response.data;
                const child = (
                    <span key="link">
                        {message}
                        &nbsp; See it&nbsp;
                        <a href={pinUrl} target="_blank" rel="noopener noreferrer">
                            here
                        </a>
                        .
                    </span>
                );
                this.notify({children: [child]});
            })
            .catch(this.messageUnsuccessfulUpload)
            .then(onComplete);
    };

    createPinterestBoard = async (url, {name, isSecret}, onComplete) => {
        try {
            const response = await axios.post(url, {name, is_secret: isSecret});
            const {boardId, boardName, isLocked} = response.data;
            const boardsPath = ['pinterest_ads', 'boards'];

            this.setState(
                {
                    shareOptions: R.assocPath(
                        boardsPath,
                        R.append(
                            {id: boardId, name: boardName, isLocked},
                            R.path(boardsPath, this.state.shareOptions)
                        ),
                        this.state.shareOptions
                    )
                },
                () => onComplete(boardId)
            );
        } catch (e) {
            this.messageUnsuccessfulUpload(e);
        }
    };

    pathFuncForMetricFromPost = {
        platform_handle: R.path([
            'content_distribution_metadata',
            'snapshots',
            'platform_account',
            'platform_handle'
        ]),
        engagement_rate: R.path(['engagement_rate']),
        engagements: R.path([
            'content_distribution_metadata',
            'snapshots',
            'social_engagements',
            'engagements'
        ]),
        post_user_score: R.path(['post_user_score']),
        ops_post_score: R.path(['ops_post_score']),
        avg_post_user_score: R.path(['avg_post_user_score'])
    };

    sortByProperty = (propertyName, sortAscending) => {
        const {posts} = this.state;
        const path = propOr(
            R.path(['content_metadata_id']),
            propertyName,
            this.pathFuncForMetricFromPost
        );

        const sort = sortAscending ? R.sortBy(path) : R.pipe(R.sortBy(path), R.reverse);
        const [sortable, unsortable] = R.partition(path, posts);

        this.setState({
            posts: R.concat(sort(sortable), unsortable),
            currentSortColumn: propertyName
        });
    };

    setSelectedPost = (contentMetadataId) => {
        const {posts} = this.state;

        const selectedIndex = R.findIndex(R.propEq('content_metadata_id', contentMetadataId))(
            posts
        );
        const selected = posts[selectedIndex];

        this.setState(
            {
                selectedPost: selected,
                showContentComment: false
            },
            this.openContentModal
        );
    };

    setSelectedPostAndComment = (contentMetadataId) => {
        const {posts} = this.state;

        const selectedIndex = R.findIndex(R.propEq('content_metadata_id', contentMetadataId))(
            posts
        );
        const selected = posts[selectedIndex];

        this.setState(
            {
                selectedPost: selected,
                showContentComment: true
            },
            this.openContentModal
        );
    };

    showBidAndBudgetRow = (isDateRangeActive) => {
        const showRow = !isDateRangeActive;

        this.setState({
            showBidAndBudgetRow: showRow
        });
    };

    handleDownloadContent = () => {
        this.setState({
            showDownloadSurvey: true
        });
    };

    closeSubmitContentDownloadSurvey = () => {
        this.setState({
            showDownloadSurvey: false
        });
    };

    openContentModal = () => {
        this.setState({
            showContentModal: true
        });
    };

    closeContentModal = () => {
        this.setState({
            showContentModal: false,
            showDownloadSurvey: false,
            showDownloadSurveyThankYou: false
        });
    };

    displayProgressBar() {
        const {progress} = this.props;

        if (R.isNil(progress)) return null;

        if (progress.show_tracker) {
            return (
                <ProgressBar
                    progress={progress}
                    flightEnd={this.props.campaignProgress.flightEnd}
                    fullStartDate={this.props.analyticsData.full_start_date}
                    fullEndDate={this.props.analyticsData.full_end_date}
                />
            );
        }
        return null;
    }

    shareContentModalBody() {
        const {analyticsData} = this.props;

        return (
            <span>
                Anybody shared will be able to view and download content for the&nbsp;
                <b>{analyticsData.campaign_name}</b> campaign
            </span>
        );
    }

    handleCopyToClipboard = async (text) => {
        const {notifications} = this.state;
        let notification;
        let updatedNotifications;

        try {
            await navigator.clipboard.writeText(text);
            notification = {
                id     : new Date().getTime(),
                message: 'Copied to clipboard.',
                type   : 'success'
            };
        } catch (e) {
            notification = {
                id     : new Date().getTime(),
                message: 'An error occurred while copying to clipboard.',
                type   : 'error'
            };
        } finally {
            updatedNotifications = notifications.concat([
                notification
            ]);

            this.setState({
                notifications: updatedNotifications
            });
        }
    };

    render() {
        const {
            analyticsData: {campaign_id},
            isRateCreatorsDisabled
        } = this.props;
        const {analyticsData} = this.state;
        const shareContentModalHeader = 'Share Your Campaign';
        const shareContentModalBody = this.shareContentModalBody();
        const progressBar = this.displayProgressBar();
        const {isAdmin, isAgency, isBrand, isSalesUser, urls} = navigationMenu;
        const advancedAnalyticsUrl = `/brands/campaigns/${campaign_id}/advanced-analytics`;

        return (
            <div>
                <Notification notifications={this.state.notifications} />
                <NavigationMenu
                    activeKey="analytics"
                    campaign={{id: campaign_id}}
                    isAdmin={isAdmin}
                    isSalesUser={isSalesUser}
                    urls={urls}
                />
                <AnalyticsHeaderContainer
                    brandName={this.props.analyticsData.brand_name}
                    campaignName={this.props.analyticsData.campaign_name}
                    isAdmin={this.props.isAdmin}
                    isSalesUser={this.props.isSalesUser}
                    campaignWrapUpUrl={this.props.campaignWrapUpUrl}
                    showCampaignWrapUp={this.props.showCampaignWrapUp}
                    isWrapUpBrandVisible={this.props.isWrapUpBrandVisible}
                    handleShareCampaignShow={this.handleShareCampaignShow}
                    handleDateRangeChange={this.handleDateRangeChange}
                    showBidAndBudgetRow={this.showBidAndBudgetRow}
                    advancedAnalyticsUrl={advancedAnalyticsUrl}
                    isCampaignAnalyticsPage
                    contentWorkflow={this.props.analyticsData.campaign_content_workflow}
                    fullStartDate={this.props.analyticsData.full_start_date}
                    isTikTok={this.state.campaignType === 'tiktok'}
                    campaignCode={this.state.tiktokCampaignCode}
                    inviteLink={this.state.tiktokInviteLink}
                />
                <AnalyticsContainer
                    bid={analyticsData.bid}
                    budget={analyticsData.budget}
                    campaignType={analyticsData.platform_type}
                    contentWorkflow={this.props.analyticsData.campaign_content_workflow}
                    countInternalVisiblePosts={analyticsData.count_internal_visible_posts}
                    countBrandVisiblePosts={analyticsData.count_brand_visible_posts}
                    ecpx={analyticsData.ecpx}
                    engagementsPercentage={analyticsData.engagements_percentage}
                    flightEnd={this.props.campaignProgress.flightEnd}
                    koer={analyticsData.koer}
                    loading={this.state.loading}
                    objectivBidType={analyticsData.objective_bid_type}
                    percentDelivered={analyticsData.percent_delivered}
                    showKoer={analyticsData.show_koer}
                    showViews={this.props.analyticsData.video_views_are_engagements}
                    showBidAndBudgetRow={this.state.showBidAndBudgetRow}
                    totalComments={analyticsData.total_comments}
                    totalEngagements={analyticsData.total_engagements}
                    totalLikes={analyticsData.total_likes}
                    totalReach={analyticsData.total_reach}
                    totalViews={analyticsData.total_views}
                    totalScreenshots={analyticsData.total_screenshots}
                    totalImpressions={analyticsData.total_impressions}
                    totalPotentialExposure={analyticsData.total_potential_exposure}
                    totalViewers={analyticsData.total_viewers}
                    totalShares={analyticsData.total_shares}
                    totalPlays={analyticsData.total_plays}
                    totalTrueReach={analyticsData.total_true_reach}
                    userCount={analyticsData.user_count}
                    isSalesUser={this.props.isSalesUser}
                    brandId={this.props.analyticsData.brand_id}
                    isTikTok={this.state.campaignType === 'tiktok'}
                />
                {progressBar}
                <AnalyticsPostsContainer
                    campaignId={this.props.analyticsData.campaign_id}
                    campaignName={this.props.analyticsData.campaign_name}
                    contentWorkflow={this.props.analyticsData.campaign_content_workflow}
                    brandName={this.props.analyticsData.brand_name}
                    closeContentModal={this.closeContentModal}
                    closeSubmitContentDownloadSurvey={this.closeSubmitContentDownloadSurvey}
                    currentSortColumn={this.state.currentSortColumn}
                    downloadUrl={this.props.downloadUrl}
                    rateCreatorUrl={this.props.rateCreatorUrl}
                    commentOnCreatorUrl={this.props.commentOnCreatorUrl}
                    email={this.props.email}
                    fullStartDate={this.props.analyticsData.full_start_date}
                    fullEndDate={this.props.analyticsData.full_end_date}
                    isAdmin={isAdmin}
                    isAgency={isAgency}
                    isBrand={isBrand}
                    isRateCreatorsDisabled={isRateCreatorsDisabled}
                    isSalesUser={this.props.isSalesUser}
                    isContentGalleryPage={false}
                    loading={this.state.loading}
                    posts={this.state.posts}
                    selectedPost={this.state.selectedPost}
                    setPostScore={this.setPostScore}
                    setContentComment={this.setContentComment}
                    handleDateRangeChange={this.handleDateRangeChange}
                    handleDownloadContent={this.handleDownloadContent}
                    handleSubmitContentDownloadSurvey={this.handleSubmitContentDownloadSurvey}
                    shareToFacebookAdManager={this.shareToFacebookAdManager}
                    shareToFacebookPagesAccount={this.shareToFacebookPagesAccount}
                    shareToTwitterAdManager={this.shareToTwitterAdManager}
                    shareToSnapchatAdManager={this.shareToSnapchatAdManager}
                    shareToPinterest={this.shareToPinterest}
                    createPinterestBoard={this.createPinterestBoard}
                    showBidAndBudgetRow={this.showBidAndBudgetRow}
                    showPostScore
                    setSelectedPost={this.setSelectedPost}
                    setSelectedPostAndComment={this.setSelectedPostAndComment}
                    showContentModal={this.state.showContentModal}
                    showDownloadSurvey={this.state.showDownloadSurvey}
                    showDownloadSurveyThankYou={this.state.showDownloadSurveyThankYou}
                    sortByProperty={this.sortByProperty}
                    shareOptions={this.state.shareOptions}
                    showKoer={this.props.analyticsData.show_koer}
                    showViews={this.props.analyticsData.video_views_are_engagements}
                    selectedStart={this.state.selectedStart}
                    selectedEnd={this.state.selectedEnd}
                    dateSelectedIdx={this.state.dateSelectedIdx}
                    dateSelectActive={this.state.dateSelectActive}
                    hideDateSelect={this.hideDateSelect}
                    showContentComment={this.state.showContentComment}
                    selectedPostComment={this.state.selectedPostComment}
                    campaignType={this.state.campaignType}
                    handleRequestSparkAdAuth={this.handleRequestSparkAdAuth}
                    handleCopyToClipboard={this.handleCopyToClipboard}
                />
                <ShareContentModal
                    campaignId={this.props.analyticsData.campaign_id}
                    campaignName={this.props.analyticsData.campaign_name}
                    brandName={this.props.analyticsData.brand_name}
                    allShareTokens={this.props.allShareTokens}
                    header={shareContentModalHeader}
                    body={shareContentModalBody}
                    genericShareableLink={this.state.genericShareableLink}
                    show={this.state.showShareCampaign}
                    onHide={this.handleShareCampaignHide}
                    onShareSucceed={this.handleShareSucceed}
                    displayEmailDeletedSuccess={this.displayEmailDeletedSuccess}
                    email={this.props.email}
                    handleGetShareableLinkClick={this.handleGetShareableLinkClick}
                    handleRemoveShareableLinkClick={this.handleRemoveShareableLinkClick}
                />
            </div>
        );
    }
}

CampaignAnalyticsPage.propTypes = {
    allShareTokens: PropTypes.array,
    analyticsData: PropTypes.shape({
        bid: PropTypes.number.isRequired,
        bid_type: PropTypes.string.isRequired,
        brand_id: PropTypes.number.isRequired,
        brand_industry: PropTypes.string,
        brand_name: PropTypes.string.isRequired,
        budget: PropTypes.number.isRequired,
        campaign_id: PropTypes.number.isRequired,
        campaign_name: PropTypes.string.isRequired,
        campaign_content_workflow: PropTypes.string.isRequired,
        count_brand_visible_posts: PropTypes.number.isRequired,
        ecpx: PropTypes.number.isRequired,
        engagements: PropTypes.number.isRequired,
        engagements_percentage: PropTypes.number.isRequired,
        full_end_date: PropTypes.string.isRequired,
        full_start_date: PropTypes.string.isRequired,
        influencer_compensation_type: PropTypes.string,
        koer: PropTypes.number.isRequired,
        last_updated: PropTypes.string,
        objective: PropTypes.string,
        objective_bid: PropTypes.number.isRequired,
        objective_bid_type: PropTypes.string.isRequired,
        percent_delivered: PropTypes.number.isRequired,
        percent_duration_over: PropTypes.number,
        platform_type: PropTypes.string.isRequired,
        post_analytics: PropTypes.arrayOf(PropTypes.shape(POST)).isRequired,
        pretty_end_date: PropTypes.string.isRequired,
        pretty_start_date: PropTypes.string.isRequired,
        show_koer: PropTypes.number.isRequired,
        total_comments: PropTypes.number.isRequired,
        total_engagements: PropTypes.number.isRequired,
        total_likes: PropTypes.number.isRequired,
        total_new_followers: PropTypes.number.isRequired,
        total_reach: PropTypes.number.isRequired,
        total_repurposes: PropTypes.number,
        total_retweets: PropTypes.number,
        total_screenshots: PropTypes.number,
        total_views: PropTypes.number.isRequired,
        user_count: PropTypes.number,
        value_deliverered: PropTypes.number,
        video_views_are_engagements: PropTypes.number.isRequired,
        total_potential_exposure: PropTypes.number.isRequired,
        total_viewers: PropTypes.number.isRequired,
        total_shares: PropTypes.number.isRequired,
        total_plays: PropTypes.number.isRequired,
    }),
    email: PropTypes.string.isRequired,
    campaignProgress: PropTypes.object,
    genericShareableLink: PropTypes.string,
    isAdmin: PropTypes.bool,
    isRateCreatorsDisabled: PropTypes.bool,
    isSalesUser: PropTypes.bool,
    progress: PropTypes.object,
    createNewDownloadSurveyUrl: PropTypes.string
};

export default CampaignAnalyticsPage;



// WEBPACK FOOTER //
// ./campaign_analytics/v2/campaign_analytics_page/index.js